import { useState } from 'react'
import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { downloadCsvReport } from '../../../helpers/downloadFile'
import s from './styles.module.scss'

// Optional second download button rendered directly beneath the primary green
// one. Used on the PCP Referral overview to offer the "All Clients Eligibility"
// export to managers — never passed on the team-leader card, so it stays hidden
// at team level.
interface SecondaryDownload {
    label: string
    requestBody: Record<string, unknown>
    filenamePrefix: string
    endpoint?: string
}

interface Props {
    title: string
    // Headline count of clients with claims. Undefined renders an em dash.
    count?: number
    subtitle?: string
    // Download endpoint + request body. Defaults to the shared /download-report
    // route; the team-leader box overrides this with its dedicated route.
    endpoint?: string
    requestBody?: Record<string, unknown>
    // Filename slug (date is appended).
    filenamePrefix: string
    // When supplied, renders an extra button below the primary download.
    secondaryDownload?: SecondaryDownload
}

const formatNumber = (n?: number) =>
    typeof n === 'number' && !isNaN(n) ? new Intl.NumberFormat('en-GB').format(n) : '—'

export const ClientsWithClaimsStat = ({
    title,
    count,
    subtitle = 'Clients with at least one policy-bearing claim. Download the full list as a spreadsheet.',
    endpoint = '/download-report',
    requestBody = {},
    filenamePrefix,
    secondaryDownload
}: Props) => {
    const [downloading, setDownloading] = useState(false)
    const [secondaryDownloading, setSecondaryDownloading] = useState(false)

    const handleDownload = async () => {
        setDownloading(true)
        Loading.standard('Generating spreadsheet...')
        try {
            const filename = `${filenamePrefix}-${new Date().toISOString().slice(0, 10)}.csv`
            await downloadCsvReport(endpoint, requestBody, filename)
            Report.success('Success', 'Spreadsheet generated successfully!', 'OK')
        } catch (error: any) {
            console.error('Error downloading clients with claims report:', error)
            Report.failure(
                'Error',
                error?.response?.data?.message || 'Failed to generate spreadsheet. Please try again.',
                'OK'
            )
        } finally {
            setDownloading(false)
            Loading.remove()
        }
    }

    const handleSecondaryDownload = async () => {
        if (!secondaryDownload) return
        setSecondaryDownloading(true)
        Loading.standard('Generating report...')
        try {
            const filename = `${secondaryDownload.filenamePrefix}-${new Date().toISOString().slice(0, 10)}.csv`
            await downloadCsvReport(secondaryDownload.endpoint || '/download-report', secondaryDownload.requestBody, filename)
            Report.success('Success', 'Report generated successfully!', 'OK')
        } catch (error: any) {
            console.error('Error downloading eligibility report:', error)
            Report.failure(
                'Error',
                error?.response?.data?.message || 'Failed to generate report. Please try again.',
                'OK'
            )
        } finally {
            setSecondaryDownloading(false)
            Loading.remove()
        }
    }

    return (
        <div className={s.wrapper}>
            <div className={s.card}>
                <div className={s.accentBar} />
                <div className={s.header}>
                    <div>
                        <div className={s.title}>{title}</div>
                        <div className={s.subtitle}>{subtitle}</div>
                    </div>
                    <div className={s.buttonColumn}>
                        <button type="button" className={s.downloadButton} onClick={handleDownload} disabled={downloading}>
                            {downloading ? 'Generating…' : 'Download spreadsheet'}
                        </button>
                        {secondaryDownload && (
                            <button
                                type="button"
                                className={s.secondaryButton}
                                onClick={handleSecondaryDownload}
                                disabled={secondaryDownloading}
                            >
                                {secondaryDownloading ? 'Generating…' : secondaryDownload.label}
                            </button>
                        )}
                    </div>
                </div>
                <div className={s.valueRow}>
                    <div className={s.value}>{formatNumber(count)}</div>
                    <div className={s.outOf}>clients with claims</div>
                </div>
            </div>
        </div>
    )
}

export default ClientsWithClaimsStat
